/**
 * Serviço para integração com Rest PKI Core (Docker)
 * API da Lacuna Software para validação de certificados digitais
 */

import axios from 'axios';

class RestPKICoreService {
  constructor() {
    // Usar sempre a URL direta do Rest PKI Core
    this.baseURL = process.env.VUE_APP_REST_PKI_URL || 'https://pki.cenprotnacional.org.br';
    this.apiKey = process.env.VUE_APP_REST_PKI_API_KEY;
    
    console.log('Rest PKI Core configurado para:', this.baseURL);
    
    // Criar instância do axios para Rest PKI Core
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 30000
    });

    // Adicionar API Key no header se fornecida e não vazia
    if (this.apiKey && this.apiKey.trim() !== '' && this.apiKey !== 'sua-api-key-aqui') {
      this.api.defaults.headers['X-Api-Key'] = this.apiKey;
      console.log('Rest PKI: API Key configurada');
    } else {
      console.warn('Rest PKI: Operando sem API Key - algumas funcionalidades podem não estar disponíveis');
    }
  }

  /**
   * Inicia uma sessão de autenticação com certificado
   */
  async iniciarAutenticacao() {
    try {
      const response = await this.api.post('/api/authentications', {
        securityContextId: 'lacuna-default', // ID do contexto de segurança
        certificateWithKey: true // Exigir certificado com chave privada
      });
      
      return {
        sessionId: response.data.id,
        nonce: response.data.nonce,
        digestAlgorithm: response.data.digestAlgorithm || 'SHA256',
        certificateEncoding: response.data.certificateEncoding || 'Base64'
      };
    } catch (error) {
      const errorMessage = error.response && error.response.data && error.response.data.message 
        ? error.response.data.message 
        : error.message;
      throw new Error(`Erro ao iniciar autenticação: ${errorMessage}`);
    }
  }

  /**
   * Completa a autenticação enviando o certificado e assinatura
   */
  async completarAutenticacao(sessionId, certificate, signature) {
    try {
      const response = await this.api.post(`/api/authentications/${sessionId}/complete`, {
        certificate: certificate,
        signature: signature
      });
      
      if (response.data.validationResults && response.data.validationResults.isValid) {
        return {
          success: true,
          certificate: response.data.certificate,
          validationResults: response.data.validationResults,
          userInfo: this.extrairInformacoesCertificado(response.data.certificate)
        };
      } else {
        const errorMessage = response.data.validationResults && response.data.validationResults.errors && response.data.validationResults.errors[0]
          ? response.data.validationResults.errors[0].message
          : 'Erro desconhecido';
        throw new Error(`Certificado inválido: ${errorMessage}`);
      }
    } catch (error) {
      const errorMessage = error.response && error.response.data && error.response.data.message 
        ? error.response.data.message 
        : error.message;
      throw new Error(`Erro ao completar autenticação: ${errorMessage}`);
    }
  }

  /**
   * Valida um certificado diretamente (sem assinatura)
   */
  async validarCertificado(certificateBase64) {
    try {
      const response = await this.api.post('/api/certificates/validate', {
        certificate: certificateBase64,
        securityContextId: 'lacuna-default'
      });
      
      return {
        isValid: response.data.validationResults && response.data.validationResults.isValid || false,
        certificate: response.data.certificate,
        validationResults: response.data.validationResults,
        userInfo: this.extrairInformacoesCertificado(response.data.certificate)
      };
    } catch (error) {
      const errorMessage = error.response && error.response.data && error.response.data.message 
        ? error.response.data.message 
        : error.message;
      throw new Error(`Erro ao validar certificado: ${errorMessage}`);
    }
  }

  /**
   * Realiza login completo com Rest PKI usando certificado Web PKI
   * Retorna sessionId para validação posterior no backend
   */
  async fazerLoginCompleto(certificado) {
    try {
      // Etapa 1: Iniciar sessão de autenticação
      console.log('🔐 Iniciando sessão de autenticação no Rest PKI...');
      const sessao = await this.iniciarAutenticacao();
      
      console.log('📋 Sessão criada:', {
        sessionId: sessao.sessionId,
        nonce: sessao.nonce.substring(0, 20) + '...'
      });

      // Etapa 2: Assinar o nonce com Web PKI
      console.log('✍️ Assinando nonce com Web PKI...');
      const WebPKI = window.lacunaWebPKI;
      
      // Configurar operação de assinatura
      const signOperation = WebPKI.signatureStarter.setSignatureAlgorithm('SHA256withRSA');
      signOperation.setCertificate(certificado);
      signOperation.setToSignHash(sessao.nonce, sessao.digestAlgorithm || 'SHA256');
      
      // Executar assinatura
      const assinatura = await new Promise((resolve, reject) => {
        signOperation.start({
          success: (signature) => {
            console.log('✅ Assinatura gerada com sucesso');
            resolve(signature);
          },
          error: (error) => {
            console.error('❌ Erro na assinatura:', error);
            reject(new Error('Erro ao assinar nonce: ' + error.message));
          }
        });
      });

      // Etapa 3: Completar autenticação no Rest PKI
      console.log('🔗 Completando autenticação no Rest PKI...');
      const resultado = await this.completarAutenticacao(
        sessao.sessionId, 
        certificado.encoding, 
        assinatura
      );

      // Etapa 4: Armazenar sessionId para validação posterior
      const loginData = {
        sessionId: sessao.sessionId,
        timestamp: new Date().toISOString(),
        certificate: {
          thumbprint: certificado.thumbprint,
          serialNumber: certificado.serialNumber,
          subject: certificado.subjectName
        },
        userInfo: resultado.userInfo,
        validationResults: resultado.validationResults
      };

      // Armazenar temporariamente no sessionStorage para o backend verificar
      sessionStorage.setItem('restpki_session', JSON.stringify(loginData));
      
      console.log('🎉 Login Rest PKI completo! SessionId:', sessao.sessionId);

      return {
        success: true,
        sessionId: sessao.sessionId,
        userInfo: resultado.userInfo,
        certificate: resultado.certificate,
        validationResults: resultado.validationResults,
        loginData: loginData
      };

    } catch (error) {
      console.error('❌ Erro no login Rest PKI:', error);
      throw new Error(`Falha no login Rest PKI: ${error.message}`);
    }
  }

  /**
   * Extrai informações importantes do certificado
   */
  extrairInformacoesCertificado(certificateData) {
    if (!certificateData) return null;

    const subject = certificateData.subjectName || {};
    const issuer = certificateData.issuerName || {};

    return {
      // Dados principais
      nome: subject.commonName || 'Nome não identificado',
      email: subject.emailAddress || null,
      cpf: this.extrairCPF(certificateData),
      cnpj: this.extrairCNPJ(certificateData),
      
      // Dados do certificado
      serialNumber: certificateData.serialNumber,
      thumbprint: certificateData.thumbprint,
      
      // Validade
      validFrom: certificateData.validityStart,
      validTo: certificateData.validityEnd,
      isValid: new Date(certificateData.validityEnd) > new Date(),
      
      // Emissor
      issuer: issuer.commonName || 'Emissor não identificado',
      
      // Tipo de certificado
      keyUsage: certificateData.keyUsage || [],
      enhancedKeyUsage: certificateData.enhancedKeyUsage || [],
      
      // Dados brutos
      subjectName: certificateData.subjectName,
      issuerName: certificateData.issuerName,
      
      // Determinar tipo de pessoa
      tipoPessoa: this.extrairCNPJ(certificateData) ? 'CNPJ' : 'CPF'
    };
  }

  /**
   * Extrai CPF do certificado (vários formatos possíveis)
   */
  extrairCPF(certificateData) {
    const sources = [
      certificateData.subjectName && certificateData.subjectName.commonName,
      certificateData.subjectName && certificateData.subjectName.serialNumber,
      certificateData.subject,
      JSON.stringify(certificateData.extensions || {})
    ].filter(Boolean);

    for (const source of sources) {
      const cpfPatterns = [
        /CPF[:\s]*(\d{3}\.?\d{3}\.?\d{3}-?\d{2})/i,
        /(\d{3}\.?\d{3}\.?\d{3}-?\d{2})/,
        /OID\.2\.16\.76\.1\.3\.1[=:\s]*(\d{11})/i
      ];

      for (const pattern of cpfPatterns) {
        const match = String(source).match(pattern);
        if (match) {
          const cpf = match[1].replace(/\D/g, '');
          if (cpf.length === 11) {
            return cpf;
          }
        }
      }
    }

    return null;
  }

  /**
   * Extrai CNPJ do certificado
   */
  extrairCNPJ(certificateData) {
    const sources = [
      certificateData.subjectName && certificateData.subjectName.commonName,
      certificateData.subjectName && certificateData.subjectName.serialNumber,
      certificateData.subject,
      JSON.stringify(certificateData.extensions || {})
    ].filter(Boolean);

    for (const source of sources) {
      const cnpjPatterns = [
        /CNPJ[:\s]*(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})/i,
        /(\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2})/,
        /OID\.2\.16\.76\.1\.3\.3[=:\s]*(\d{14})/i
      ];

      for (const pattern of cnpjPatterns) {
        const match = String(source).match(pattern);
        if (match) {
          const cnpj = match[1].replace(/\D/g, '');
          if (cnpj.length === 14) {
            return cnpj;
          }
        }
      }
    }

    return null;
  }

  /**
   * Lista certificados válidos (integração com Web PKI)
   */
  async listarCertificadosValidos(certificados) {
    const certificadosValidados = [];
    
    for (const cert of certificados) {
      try {
        const resultado = await this.validarCertificado(cert.encoding);
        if (resultado.isValid) {
          certificadosValidados.push({
            ...cert,
            restPKIValidation: resultado,
            userInfo: resultado.userInfo
          });
        }
      } catch (error) {
        console.warn(`Erro ao validar certificado ${cert.thumbprint}:`, error.message);
      }
    }
    
    return certificadosValidados;
  }

  /**
   * Obtém informações do servidor Rest PKI Core
   */
  async obterInfoServidor() {
    try {
      const response = await this.api.get('/api/system/info');
      return {
        version: response.data.version,
        productName: response.data.productName,
        environment: response.data.environment,
        serverTime: response.data.serverTime,
        authenticated: true
      };
    } catch (error) {
      // Tratar especificamente erro 401 (não autorizado)
      if (error.response && error.response.status === 401) {
        return {
          version: 'Desconhecida',
          productName: 'Rest PKI Core',
          environment: 'Não autorizado - API Key necessária',
          serverTime: new Date().toISOString(),
          authenticated: false,
          requiresAuth: true,
          error: 'Servidor Rest PKI Core requer autenticação via API Key'
        };
      }
      
      const errorMessage = error.response && error.response.data && error.response.data.message 
        ? error.response.data.message 
        : error.message;
      throw new Error(`Erro ao obter informações do servidor: ${errorMessage}`);
    }
  }

  /**
   * Testa conectividade com Rest PKI Core (endpoints públicos)
   */
  async testarConectividade() {
    try {
      console.log('🔍 Testando conectividade com Rest PKI Core...');
      
      // Tentar endpoint de health check (geralmente público)
      try {
        const healthResponse = await this.api.get('/health');
        return {
          status: 'conectado',
          endpoint: '/health',
          message: 'Servidor Rest PKI Core respondendo normalmente',
          requiresAuth: false
        };
      } catch (healthError) {
        console.log('Health check falhou, tentando info do sistema...');
      }
      
      // Tentar endpoint de informações do sistema
      try {
        const infoResponse = await this.api.get('/api/system/info');
        return {
          status: 'conectado_autenticado', 
          endpoint: '/api/system/info',
          message: 'Servidor Rest PKI Core acessível com autenticação',
          version: infoResponse.data.version,
          requiresAuth: false
        };
      } catch (infoError) {
        if (infoError.response && infoError.response.status === 401) {
          return {
            status: 'conectado_sem_auth',
            endpoint: '/api/system/info', 
            message: 'Servidor Rest PKI Core encontrado mas requer API Key',
            requiresAuth: true,
            authError: 'API Key não configurada ou inválida'
          };
        }
      }
      
      throw new Error('Nenhum endpoint acessível encontrado');
      
    } catch (error) {
      return {
        status: 'erro_conexao',
        message: 'Não foi possível conectar com Rest PKI Core',
        error: error.message,
        url: this.baseURL
      };
    }
  }

  /**
   * Inicia uma sessão de autenticação no Rest PKI Core
   */
  async iniciarAutenticacao() {
    try {
      const response = await this.api.post('/api/authentications', {
        securityContextId: 'lacuna-default'
      });
      
      return {
        sessionId: response.data.id,
        nonce: response.data.nonce,
        digestAlgorithm: response.data.digestAlgorithm || 'SHA256'
      };
    } catch (error) {
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message;
      throw new Error(`Erro ao iniciar autenticação: ${errorMessage}`);
    }
  }

  /**
   * Completa a autenticação com certificado e assinatura
   */
  async completarAutenticacao(sessionId, certificateBase64, signature) {
    try {
      const response = await this.api.post(`/api/authentications/${sessionId}/complete`, {
        certificate: certificateBase64,
        signature: signature
      });
      
      return {
        isValid: (response.data.validationResults && response.data.validationResults.isValid) || false,
        certificate: response.data.certificate,
        validationResults: response.data.validationResults,
        userInfo: this.extrairInformacoesCertificado(response.data.certificate)
      };
    } catch (error) {
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message;
      throw new Error(`Erro ao completar autenticação: ${errorMessage}`);
    }
  }

  /**
   * Cria um nonce simples para assinatura (método alternativo)
   */
  async gerarNonce() {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2);
    return `${timestamp}-${random}`;
  }

  /**
   * Valida uma assinatura com Rest PKI Core
   */
  async validarAssinatura(dados, assinatura, certificado) {
    try {
      const response = await this.api.post('/api/signatures/validate', {
        signature: assinatura,
        signedData: dados,
        certificate: certificado,
        securityContextId: 'lacuna-default'
      });
      
      return {
        isValid: (response.data.validationResults && response.data.validationResults.isValid) || false,
        validationResults: response.data.validationResults,
        certificate: response.data.certificate
      };
    } catch (error) {
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message;
      throw new Error(`Erro ao validar assinatura: ${errorMessage}`);
    }
  }

  /**
   * Verifica se uma sessão Rest PKI é válida (método estático para uso no backend)
   */
  static async verificarSessao(sessionId, baseURL, apiKey) {
    try {
      // Em desenvolvimento, usar proxy se baseURL não fornecida
      const isDevelopment = process.env.NODE_ENV === 'development';
      let urlToUse;
      
      if (baseURL) {
        urlToUse = baseURL;
      } else if (isDevelopment) {
        urlToUse = ''; // Usar proxy
      } else {
        urlToUse = 'http://localhost:9796';
      }
      
      const api = axios.create({
        baseURL: urlToUse,
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (apiKey) {
        api.defaults.headers['X-Api-Key'] = apiKey;
      }

      const response = await api.get(`/api/authentications/${sessionId}`);
      
      return {
        isValid: response.data && response.data.completed === true,
        sessionData: response.data,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        isValid: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }
}

// Exportar instância única (singleton)
export default new RestPKICoreService();
